<template>
    <div>
        <HighlightBanner v-if="!store.getters.allFieldStepsComplete" />

        <div class="summary">
            <template v-for="section in sectionsWithSteps" :key="section.key">
                <FieldSummaryTable
                    v-if="summaryReady && section?.steps?.length"
                    :sectionTitle="section.name"
                    :sectionSteps="section.steps"
                    :is-small-summary="isSmallSummary"
                />
            </template>
        </div>

        <div class="row" v-if="additionalInfo && !store.getters.allFieldStepsComplete">
            <div class="col">
                <InfoBlock :hideIcon="true" :additionalInfoHeading="additionalInfoHeading">
                    <div v-html="additionalInfo"></div>
                </InfoBlock>
            </div>
        </div>
    </div>
</template>

<script>
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition(t) {
        return {
            name: 'summary',
            hideBack: true,
            excludeFromProgress: true,
            excludeFromJourney: false,
            route: {
                absoluteUrl: process.env.TERRITORY === 'pol' ? `/streszczenie` : `/summary`,
                name: 'apply.summary',
                meta: {
                    title: store => {
                        return store.getters.allFieldStepsComplete
                            ? t('form.sections.summary.heading')
                            : t('form.sections.loanDetails.summary')
                    },
                    subText: store => {
                        return store.getters.allFieldStepsComplete ? 'form.sections.summary.subText' : null
                    },
                    layoutType: 'large',
                },
            },
        }
    },

    setup() {
        const router = useRouter()
        const store = useStore()

        return {
            router,
            store,
        }
    },

    data() {
        return {
            // prevent load if not staying on this step to avoid pre-validating
            summaryReady: false,
        }
    },

    async created() {
        if (this.$route.query) {
            await this.store.dispatch('fillInSteps', this.$route.query)
        }

        if (!this.store.getters.hasAnySummaryData) {
            await this.store.dispatch('removeCurrentStepFromJourney')
            this.emitter.emit('nextRoute')
            return
        }

        this.summaryReady = true

        this.emitter.emit('stepLoaded', 'summary')
    },

    computed: {
        isSmallSummary() {
            // we are on the summary page in only 2 situations
            // if it's not the big summary, it's the small one
            return !this.store.state.apply.has.fullSummary
        },
        sectionsWithSteps() {
            // if we are always showing full summary, don't add the fields as it will cause validation
            // and add error messages to the next steps before any data has been entered
            if (this.store.state.apply.smallSummaryIntent == 'always') {
                return []
            }

            const journeyPath = this.store.getters.journeyInfo.getCurrentJourneyPath()
            // use map to keep order
            const sections = new Map()

            for (const step of journeyPath) {
                const section = step.section

                if (!step.section) {
                    continue
                }

                if (!sections.get(section.key)) {
                    // small summary uses only first section
                    if (this.isSmallSummary && sections.size) {
                        break
                    }

                    // create new object so we don't modify ref to original section
                    sections.set(section.key, {
                        type: section.type,
                        name: section.name,
                        key: section.key,
                        steps: [],
                    })
                }

                sections.get(section.key).steps.push(step)
            }

            this.removeEmptySections(sections)

            return Array.from(sections, function (entry) {
                return entry[1]
            })
        },
        additionalInfo() {
            return this.$t('form.sections.aboutYou.name.additionalInfo') || null
        },
        additionalInfoHeading() {
            return this.$t('form.sections.aboutYou.name.additionalInfoHeading') || null
        },
    },

    methods: {
        removeEmptySections(sections) {
            for (const [key, section] of sections) {
                if (!this.checkHasValidFields(section)) {
                    sections.delete(key)
                }
            }
        },

        checkHasValidFields(section) {
            if (!section?.steps) {
                return false
            }

            for (const step of section.steps) {
                if (step.hideOnSummary || !step?.fields) {
                    continue
                }

                for (const fieldName in step.fields) {
                    const field = step.fields[fieldName]

                    if (field?.value !== null) {
                        return true
                    }
                }
            }

            return false
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/settings/responsive';
@import '~scss/settings/variables';
@import '~scss/mixins/rem';

.summary {
    font-size: rem(16px);
    text-align: left;
    margin-top: 40px;
}

.continue-button {
    margin-top: 16px;

    @media screen and (min-width: $bp-medium) {
        margin-top: 50px;
    }
}
</style>
